import { Link, createFileRoute } from "@tanstack/react-router";

import { FilterChips } from "~/components/filter-chips";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { StatusBadge } from "~/components/status-badge";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { WEBHOOK_STATUSES } from "~/lib/enums";
import { EM_DASH, formatDateTime, formatRelative, orDash } from "~/lib/format";
import { useWebhookEvents } from "~/lib/queries";
import { WebhookEventFilterBar } from "./-filter-bar";
import { RetryWebhookAction } from "./-retry-action";
import {
  ALL,
  DEFAULT_STATUS,
  hasActiveFilters,
  toFilters,
  validateWebhookEventSearch,
  type WebhookEventSearch,
} from "./-search";

export const Route = createFileRoute("/_authed/webhook-events/")({
  validateSearch: validateWebhookEventSearch,
  component: WebhookEventsPage,
});

const COLUMNS = 8;

/**
 * What the provider told us, and what we did about it.
 *
 * Ops has never been able to see this data: webhook_events has been written by
 * every v2 handler since migration 005 and, until PRO-1023, read by nothing.
 *
 * The screen opens on failed events rather than on a chronological firehose --
 * the failures are why anyone opens it, and they are a handful of rows against a
 * table that grows with every payment. The preset is in the URL, so the link
 * still says what it shows.
 */
function WebhookEventsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = toFilters(search);
  const { data, error, isPending, isFetching, refetch } = useWebhookEvents(filters);

  /** Patches the URL, resetting to the first page whenever the filters move. */
  function patch(next: Partial<WebhookEventSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  /** Back to the screen's own default, keeping the chosen page size: that is a
   *  display preference, not a filter. */
  function clearFilters() {
    void navigate({
      search: (previous) => ({ limit: previous.limit, status: DEFAULT_STATUS }),
      replace: true,
    });
  }

  const rows = data?.items ?? [];
  const filtered = hasActiveFilters(search);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Webhook Events"
        description="Every webhook DurianPay has sent us, with the raw payload and what processing it did."
      />

      <WebhookEventFilterBar
        search={search}
        onChange={patch}
        onClear={clearFilters}
        hasFilters={filtered}
      />

      {/* ALL rather than undefined: an absent status means "not chosen", which
          is where the failed-first default applies, so clearing the filter has
          to say so explicitly. See -search.ts. */}
      <FilterChips
        value={search.status === ALL ? undefined : search.status}
        options={WEBHOOK_STATUSES}
        onChange={(next) => patch({ status: next ?? ALL })}
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Received</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Order ref</TableHead>
              <TableHead>Source IP</TableHead>
              <TableHead className="text-right">Retries</TableHead>
              <TableHead className="w-0">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  search.status === "failed" && !hasOtherFilters(search)
                    ? "No failed webhooks. Switch to All to see everything that arrived."
                    : filtered
                      ? "No webhook events match these filters."
                      : "No webhook events recorded yet."
                }
                onClear={filtered ? clearFilters : undefined}
              />
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  {/* Relative for the scan, absolute in the title for the
                      moment you start correlating with the provider's log. */}
                  <TableCell>
                    <Link
                      to="/webhook-events/$eventId"
                      params={{ eventId: row.id }}
                      title={formatDateTime(row.received_at)}
                      className="text-primary hover:underline"
                    >
                      {formatRelative(row.received_at)}
                    </Link>
                  </TableCell>

                  <TableCell className="text-muted-foreground">{row.webhook_type}</TableCell>

                  <TableCell className="max-w-[12rem] truncate text-muted-foreground">
                    {orDash(row.event_type)}
                  </TableCell>

                  <TableCell>
                    <StatusBadge status={row.status} />
                  </TableCell>

                  {/* Linked only when the reference resolves. A link that 404s
                      mid-incident reads as a missing order, when in fact the
                      reference was never a transaction id. */}
                  <TableCell className="max-w-[12rem] truncate">
                    {row.order_ref_id == null ? (
                      <span className="text-muted-foreground">{EM_DASH}</span>
                    ) : row.order_ref_resolved ? (
                      <Link
                        to="/transactions/$orderId"
                        params={{ orderId: row.order_ref_id }}
                        className="font-mono text-[0.8125rem] text-primary hover:underline"
                      >
                        {row.order_ref_id}
                      </Link>
                    ) : (
                      <span
                        className="font-mono text-[0.8125rem] text-muted-foreground"
                        title="No transaction in Altura has this order id"
                      >
                        {row.order_ref_id}
                      </span>
                    )}
                  </TableCell>

                  <TableCell className="font-mono text-[0.8125rem] text-muted-foreground">
                    {orDash(row.ip_address)}
                  </TableCell>

                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.retry_count}
                  </TableCell>

                  <TableCell className="text-right">
                    {row.retryable ? <RetryWebhookAction event={row} compact /> : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}

/** True when something other than the status preset is narrowing the table. */
function hasOtherFilters(search: WebhookEventSearch): boolean {
  const { status: _status, limit: _limit, offset: _offset, ...rest } = search;
  return Object.values(rest).some((value) => value !== undefined);
}
